// Block Hero — GSAP label animations.
// 1. As the block enters the viewport, labels "fall down" with a bounce, in an
//    asymmetrical (randomised) order.
// 2. While scrolling, each label oscillates vertically on its own phase.

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

function initBlockHero(block) {
    const labels = Array.from(block.querySelectorAll(".block-hero__label"));
    if (!labels.length) {
        block.classList.add("is-ready");
        return;
    }

    const inners = labels.map((l) => l.querySelector(".block-hero__label-inner") || l);

    // Respect reduced-motion preference: just show the labels.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        gsap.set(labels, { opacity: 1, y: 0 });
        block.classList.add("is-ready");
        return;
    }

    // Initial off-screen state — drop in from above.
    gsap.set(labels, { opacity: 0, yPercent: -160 });

    // Per-label randomness drives the asymmetrical fall order.
    const order = labels.map((_, i) => i).sort(() => Math.random() - 0.5);

    const fall = gsap.timeline({
        paused: true,
        onStart: () => block.classList.add("is-ready"),
        onComplete: () => startOscillation(),
    });

    order.forEach((index, n) => {
        fall.to(
            labels[index],
            {
                opacity: 1,
                yPercent: 0,
                duration: gsap.utils.random(0.9, 1.4),
                ease: "bounce.out",
            },
            // Asymmetrical, slightly jittered cascade.
            n * 0.12 + gsap.utils.random(0, 0.1)
        );
    });

    ScrollTrigger.create({
        trigger: block,
        start: "top 75%",
        once: true,
        onEnter: () => fall.play(),
    });

    // Continuous scroll-driven oscillation of each label (different phase/amplitude).
    function startOscillation() {
        const phases = inners.map((_, i) => i * 0.9);
        const amplitudes = inners.map(() => gsap.utils.random(8, 22));
        // Baseline captured on first update so the wave starts at y:0 — no jump
        // from the resting position when oscillation kicks in.
        let baseline = null;

        ScrollTrigger.create({
            trigger: block,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
            onUpdate: (self) => {
                const t = self.progress * Math.PI * 4;
                if (baseline === null) baseline = t;
                inners.forEach((inner, i) => {
                    const offset =
                        Math.sin(t + phases[i]) - Math.sin(baseline + phases[i]);
                    gsap.set(inner, { y: offset * amplitudes[i] });
                });
            },
        });
    }
}

function initAll() {
    document.querySelectorAll("[data-block-hero]").forEach(initBlockHero);
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
} else {
    initAll();
}
